import React, { useRef } from "react";
import { toast } from "sonner";
import { Type, Image as ImageIcon, PenLine, Minus, Square, Palette, Plus, Trash2 } from "lucide-react";
import { makeText, makeImage, makeLine, makeRect, STOCK_DESIGNS, CATEGORIES, categoryLabel } from "@/lib/studio/templates";
import { fileToDataUrl } from "@/lib/studio/render";

const Btn = ({ icon: Icon, label, onClick, testId }) => (
  <button onClick={onClick} className="ccs-btn-ghost text-xs flex items-center gap-1.5 justify-start" data-testid={testId}>
    <Icon size={12} /> {label}
  </button>
);

export function InsertToolbar({ onAdd, background, onBackground, hasSignature, onSaveSignature }) {
  const imgRef = useRef(null);
  const sigRef = useRef(null);
  const upload = async (file, label) => {
    if (!file) return;
    try { onAdd(makeImage(await fileToDataUrl(file), { label, x: 75, y: 120, w: 60, h: 35 })); }
    catch { toast.error("Could not read that image"); }
  };
  const uploadSignature = async (file) => {
    if (!file) return;
    try {
      const url = await fileToDataUrl(file);
      await onSaveSignature(url);
      onAdd(makeImage("club_signature", { label: "Signature", x: 75, y: 230, w: 60, h: 22 }));
    } catch { toast.error("Could not read that image"); }
  };
  return (
    <div className="space-y-2" data-testid="insert-toolbar">
      <div className="grid grid-cols-2 gap-1.5">
        <Btn icon={Type} label="Text" testId="insert-text" onClick={() => onAdd(makeText("New text", { y: 120 }))} />
        <Btn icon={ImageIcon} label="Club logo" testId="insert-club-logo" onClick={() => onAdd(makeImage("club_logo", { label: "Club logo", y: 120 }))} />
        <Btn icon={ImageIcon} label="Image" testId="insert-image" onClick={() => imgRef.current?.click()} />
        <Btn icon={PenLine} label="Signature" testId="insert-signature"
          onClick={() => (hasSignature ? onAdd(makeImage("club_signature", { label: "Signature", x: 75, y: 230, w: 60, h: 22 })) : sigRef.current?.click())} />
        <Btn icon={Minus} label="Line" testId="insert-line" onClick={() => onAdd(makeLine({ y: 150 }))} />
        <Btn icon={Square} label="Frame" testId="insert-rect" onClick={() => onAdd(makeRect())} />
      </div>
      <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={(e) => { upload(e.target.files?.[0], "Image"); e.target.value = ""; }} data-testid="insert-image-file" />
      <input ref={sigRef} type="file" accept="image/*" className="hidden" onChange={(e) => { uploadSignature(e.target.files?.[0]); e.target.value = ""; }} data-testid="insert-signature-file" />
      <label className="flex items-center gap-2 text-xs muted-text">
        <Palette size={12} /> Page colour
        <input type="color" value={background || "#ffffff"} onChange={(e) => onBackground(e.target.value)} className="h-6 w-10 bg-transparent cursor-pointer" data-testid="page-background" />
      </label>
    </div>
  );
}

export function SignatureLibrary({ signature, onSave, onClear }) {
  const ref = useRef(null);
  const pick = async (file) => {
    if (!file) return;
    try { await onSave(await fileToDataUrl(file)); }
    catch { toast.error("Could not read that image"); }
  };
  return (
    <div className="space-y-2" data-testid="signature-library">
      <div className="muted-text text-[11px]">The chairman's signature is saved once and appears on every certificate's signature slot.</div>
      <div className="flex items-center justify-center bg-white" style={{ height: 56, border: "1px solid #2a2a2a" }} data-testid="signature-preview">
        {signature ? <img src={signature} alt="Saved signature" style={{ maxHeight: 48, maxWidth: "90%", objectFit: "contain" }} />
          : <span className="text-[10px] uppercase tracking-wider text-neutral-400" data-testid="signature-empty">No signature saved</span>}
      </div>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} data-testid="signature-file" />
      <div className="grid grid-cols-2 gap-1.5">
        <button className="ccs-btn-ghost text-xs flex items-center justify-center gap-1" onClick={() => ref.current?.click()} data-testid="signature-upload">
          <PenLine size={12} /> {signature ? "Replace" : "Upload"}
        </button>
        <button className="ccs-btn-ghost text-xs" disabled={!signature} onClick={onClear} data-testid="signature-clear">Remove</button>
      </div>
    </div>
  );
}

export function DesignsPanel({ saved, current, category, onCategory, onLoad, onNew, onApplyLook, onDelete }) {
  const list = saved.filter((d) => (d.category || "general") === category);
  const active = (id) => (current?.id === id ? "border-amber-400 text-amber-400" : "border-neutral-800 hover:border-neutral-600");
  return (
    <div className="space-y-3" data-testid="designs-panel">
      <div className="grid grid-cols-2 gap-1" data-testid="design-category-tabs">
        {CATEGORIES.map((c) => (
          <button key={c.key} onClick={() => onCategory(c.key)} data-testid={`design-cat-${c.key}`}
            className={`text-[11px] py-1 border ${category === c.key ? "border-amber-400 text-amber-400" : "border-neutral-800 muted-text"}`}>{c.label}</button>
        ))}
      </div>
      {list.length === 0 ? (
        <div className="muted-text text-xs" data-testid="saved-empty">No {categoryLabel(category).toLowerCase()} certificates yet — add one below.</div>
      ) : (
        <ul className="space-y-1">
          {list.map((d) => (
            <li key={d.id} className={`flex items-center border ${active(d.id)}`} data-testid={`saved-design-${d.id}`}>
              <button onClick={() => onLoad(d)} className="flex-1 text-left text-xs px-2 py-1.5 truncate" data-testid={`saved-design-load-${d.id}`}>{d.name}</button>
              <button onClick={() => onDelete(d)} className="px-2 muted-text hover:text-red-400" title="Delete" data-testid={`saved-design-delete-${d.id}`}><Trash2 size={12} /></button>
            </li>
          ))}
        </ul>
      )}
      <div>
        <div className="ccs-label text-[10px] mb-1.5">New certificate from a look</div>
        <div className="grid grid-cols-2 gap-1.5">
          {STOCK_DESIGNS.map((s) => (
            <button key={s.id} onClick={() => onNew(s)} data-testid={`stock-${s.id}`}
              className="text-xs py-1.5 px-2 border border-neutral-800 hover:border-neutral-600 text-left flex items-center gap-1"><Plus size={10} /> {s.name}</button>
          ))}
          <button onClick={() => onNew(null)} data-testid="stock-blank"
            className="text-xs py-1.5 px-2 border border-dashed border-neutral-700 muted-text text-left col-span-2 flex items-center gap-1"><Plus size={10} /> Blank page</button>
        </div>
      </div>
      {current?.id && (
        <div>
          <div className="ccs-label text-[10px] mb-1.5">Restyle this certificate</div>
          <select className="ccs-input text-xs w-full" value="" data-testid="apply-look-select"
            onChange={(e) => { const s = STOCK_DESIGNS.find((x) => x.id === e.target.value); if (s) onApplyLook(s); }}>
            <option value="">Replace layout with a stock look…</option>
            {STOCK_DESIGNS.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      )}
    </div>
  );
}
